// Copyright 2022 Sorama B.V.

import { autoinject, bindable, bindingMode, computedFrom, customElement, observable } from "aurelia-framework";

enum PromiseState {
    Empty = "empty",
    Pending = "pending",
    Fulfilled = "fulfilled",
    Rejected = "rejected",
}

export type Position = "left" | "center" | "right";

@customElement("promise-button")
@autoinject
export class PromiseButton {
    @bindable({ defaultBindingMode: bindingMode.twoWay, defaultValue: null })
    public promise: unknown;

    @bindable
    public showFulfilled = false;
    @bindable
    public showRejected = false;
    @bindable
    public resetTime = 0;
    @bindable
    public disabled = false;
    @bindable
    public iconPosition: Position = "left";
    @bindable
    public iconClass = "";

    @observable
    private promiseState = PromiseState.Empty;

    @bindable
    protected readonly pendingText: string;
    @bindable
    protected readonly fulfilledText: string;
    @bindable
    protected readonly rejectedText: string;

    @computedFrom("promiseState")
    get empty(): boolean {
        return this.promiseState === PromiseState.Empty;
    }

    @computedFrom("promiseState")
    get pending(): boolean {
        return this.promiseState === PromiseState.Pending;
    }

    @computedFrom("promiseState")
    get fulfilled(): boolean {
        return this.promiseState === PromiseState.Fulfilled;
    }

    @computedFrom("promiseState")
    get rejected(): boolean {
        return this.promiseState === PromiseState.Rejected;
    }

    @computedFrom("promiseState")
    get settled(): boolean {
        return this.fulfilled || this.rejected;
    }

    @computedFrom("promiseState")
    get shouldDisplaySlot(): boolean {
        const x =
            this.empty ||
            (this.pending && !this.pendingText) ||
            (this.fulfilled && !this.fulfilledText) ||
            (this.rejected && !this.rejectedText);
        return x;
    }

    @computedFrom("promiseState")
    get buttonText(): string {
        switch (this.promiseState) {
            case PromiseState.Pending:
                return this.pendingText ?? "Loading...";
            case PromiseState.Fulfilled:
                return this.fulfilledText ?? "Success";
            case PromiseState.Rejected:
                return this.rejectedText ?? "Failed";
        }
        return "";
    }

    private onButtonClicked(): void {
        if (this.disabled) return;
        if (this.promiseState === PromiseState.Pending) return;
        if (typeof this.promise !== "function") return;

        this.promiseState = PromiseState.Pending;
        this.promise()
            .then((result: unknown) => {
                this.promiseState = this.showFulfilled ? PromiseState.Fulfilled : PromiseState.Empty;
                return result;
            })
            .catch((e) => {
                this.promiseState = this.showRejected ? PromiseState.Rejected : PromiseState.Empty;
                throw e;
            })
            .finally(() => setTimeout(() => (this.promiseState = PromiseState.Empty), this.resetTime));
    }
}
